<script setup lang="ts">
/**
 * Where a free layer stands in the flow.
 *
 * The elements themselves are drawn over the page by the sheet, so there is
 * nothing to draw here except a way of finding the layer in the flow: to
 * select it, to pick it up and move it to another page, to see how many
 * elements it holds. That is a small tab hanging in the page margin, on a
 * row of no height, so the flow is exactly as tall with the layer as without
 * it and the page prints as it did before anything was placed.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import SectionIcon from '@/components/builder/SectionIcon.vue'
import type { LayerSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: LayerSection }>()

const count = computed(() => props.section.elements.length)

const summary = computed(() => {
  if (count.value === 0) return t('canvas.layer.none')
  if (count.value === 1) return t('canvas.layer.one')
  return t('canvas.layer.many', { count: count.value })
})
</script>

<template>
  <div class="mark" :title="`${t('sections.layer')} : ${summary}`" data-flowless>
    <span class="mark__tab">
      <SectionIcon kind="layer" :size="11" />
      <span class="mark__count">{{ count }}</span>
    </span>
  </div>
</template>

<style scoped>
.mark {
  position: relative;
  height: 0;
}

/* In the left margin, clear of the text and of the block's own tag, which
   rises from the top-left corner of the measure on hover. */
.mark__tab {
  position: absolute;
  top: -3.5mm;
  left: calc(-1 * var(--page-margin-side) + 3mm);
  display: inline-flex;
  align-items: center;
  gap: 1.2mm;
  height: 7mm;
  padding: 0 2.2mm;
  border-radius: 999px;
  background: var(--paper);
  box-shadow: 0 0 0 1px var(--paper-rule);
  color: var(--paper-ink-soft);
  font-family: var(--font-ui);
  font-size: 7.5pt;
  font-weight: 600;
  white-space: nowrap;
}

@media print {
  .mark {
    display: none;
  }
}
</style>
